/**
 * PublicLangToggle — EN/ES switch for customer-facing tenant microsites.
 *
 * Reads the current path, strips/adds the "/es" segment after the site base
 * (via useThemeBaseRoot), navigates to the same page in the other language,
 * and persists the user's preference to localStorage so AutoLangRedirect
 * stops auto-redirecting once they've explicitly chosen.
 */
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Languages } from "lucide-react";
import { useThemeBaseRoot } from "../../hooks/useThemeBase";
import { useLang } from "../../contexts/I18nContext";

const LANG_PREF_KEY = "public_lang_pref";

export function rememberLangChoice(lang) {
  try { localStorage.setItem(LANG_PREF_KEY, lang); } catch { /* noop */ }
}

export function getRememberedLang() {
  try { return localStorage.getItem(LANG_PREF_KEY); } catch { return null; }
}

export default function PublicLangToggle({ className = "", compact = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = useLang("site").lang;
  const base = useThemeBaseRoot();

  const swap = (e) => {
    e?.preventDefault?.();
    // Build the same path but with the other language prefix.
    const afterBase = location.pathname.slice(base.length);
    let rest = afterBase;
    if (rest === "/es" || rest.startsWith("/es/")) {
      rest = rest.slice(3) || "/";
    }
    const target = lang === "es"
      ? `${base}${rest}${location.search}`
      : `${base}/es${rest === "/" ? "" : rest}${location.search}`;
    const next = lang === "es" ? "en" : "es";
    rememberLangChoice(next);
    navigate(target);
  };

  const label = lang === "es" ? "EN" : "ES";
  const title = lang === "es" ? "View in English" : "Ver en Español";

  return (
    <button
      type="button"
      onClick={swap}
      title={title}
      data-testid="public-lang-toggle"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider border border-black/10 hover:border-black/30 bg-white/80 backdrop-blur transition-colors ${className}`}
    >
      <Languages className="w-3.5 h-3.5" />
      {!compact && <span>{label}</span>}
    </button>
  );
}
